import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Copy, ExternalLink, Lock, Palette, ShieldCheck, Loader2 } from "lucide-react";
import { verifyUnlockToken } from "@/lib/api";
import { getUnlockToken, clearUnlockToken } from "@/lib/aiUsage";
import { secureGetJSON, secureSet } from "@/lib/secureStorage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import ListingForm from "@/components/ListingForm";
import useSEO from "@/lib/useSEO";

const CONFIG_KEY = "centennial_advisor_config_v1";

const DEFAULT_CFG = {
  brand: "Acme Wealth",
  logo: "",
  color: "#2B5339",
  hide_powered: true,
};

export default function Advisor() {
  useSEO({
    title: "Advisor Dashboard · Centennial White-Label Embed",
    description: "Manage your white-label retirement calculator embed and featured advisor listing. Requires an active Advisor or Featured Advisor token.",
    canonicalPath: "/advisor",
  });
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [packageId, setPackageId] = useState(null);
  const [token, setToken] = useState(null);
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      const tok = getUnlockToken();
      if (!tok) {
        setAuthorized(false);
        setChecking(false);
        return;
      }
      setToken(tok);
      try {
        const v = await verifyUnlockToken(tok);
        const validForAdvisor = !!(v?.valid && (v?.package_id === "advisor_monthly" || v?.package_id === "advisor_listing_yearly"));
        setAuthorized(validForAdvisor);
        setPackageId(v?.package_id ?? null);
      } catch {
        setAuthorized(false);
      } finally {
        setChecking(false);
      }
    })();
    // Load saved config
    const stored = secureGetJSON(CONFIG_KEY, null);
    if (stored) setCfg({ ...DEFAULT_CFG, ...stored });
  }, []);

  const persist = (next) => {
    setCfg(next);
    secureSet(CONFIG_KEY, next);
  };

  const set = (k, v) => persist({ ...cfg, [k]: v });

  const embedUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams();
    if (cfg.brand && cfg.brand !== DEFAULT_CFG.brand) p.set("brand", cfg.brand);
    if (cfg.brand) p.set("brand", cfg.brand);
    if (cfg.logo) p.set("logo", cfg.logo);
    if (cfg.color && cfg.color !== "#2B5339") p.set("color", cfg.color);
    if (cfg.hide_powered) p.set("hide_powered", "1");
    return `${window.location.origin}/embed?${p.toString()}`;
  }, [cfg]);

  const snippet = `<iframe
  src="${embedUrl}"
  width="100%"
  height="640"
  style="border:1px solid #E5E2DC; border-radius:16px;"
  loading="lazy"
  title="${(cfg.brand || "Retirement").replace(/"/g, "&quot;")} — retirement calculator"
></iframe>`;

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Snippet copied — paste into your site's HTML.");
    } catch {
      window.prompt("Copy this snippet:", snippet);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center text-[#5C6B62] text-sm">
        <Loader2 className="animate-spin mr-2" size={14} /> Checking your subscription…
      </div>
    );
  }

  if (!authorized) {
    return (
      <div data-testid="advisor-locked" className="min-h-screen bg-[#F9F8F6] text-[#1C2B23] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Lock size={28} className="mx-auto text-[#5C6B62]" />
          <h1 className="font-display mt-4 text-3xl tracking-tight">Advisor dashboard</h1>
          <p className="mt-3 text-[#5C6B62]">
            This page is for active Advisor White-Label subscribers. Subscribe for $19/mo to access white-label branding for your embed.
          </p>
          <Link
            to="/pricing"
            className="mt-6 inline-flex items-center gap-2 bg-[#2B5339] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#1E3B28]"
          >
            View pricing
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="advisor-page" className="min-h-screen bg-[#F9F8F6] text-[#1C2B23]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm hover:text-[#2B5339]">
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="font-display tracking-tight text-lg">
            Centennial<span className="text-[#C26747]">.</span>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-12 lg:py-16">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#5C6B62] font-semibold inline-flex items-center gap-2">
              <ShieldCheck size={12} /> advisor white-label
            </div>
            <h1 className="font-display mt-2 text-4xl sm:text-5xl tracking-tight font-medium">
              Configure your embed.
            </h1>
          </div>
          <button
            type="button"
            onClick={() => {
              clearUnlockToken();
              navigate("/pricing");
            }}
            className="text-xs text-[#5C6B62] hover:text-[#C26747]"
            data-testid="advisor-signout"
          >
            Clear stored subscription token
          </button>
        </div>
        <p className="mt-3 text-[#5C6B62] max-w-2xl">
          Customize your brand below. Changes save automatically to this browser. Copy the iframe snippet and paste it into your site's HTML.
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Form */}
          <div className="lg:col-span-5 bg-white border border-[#E5E2DC] rounded-2xl p-6 space-y-5">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">Brand name</Label>
              <Input
                data-testid="advisor-brand"
                value={cfg.brand}
                onChange={(e) => set("brand", e.target.value)}
                className="mt-2"
                placeholder="Acme Wealth"
              />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">Logo URL (square, 64×64+)</Label>
              <Input
                data-testid="advisor-logo"
                value={cfg.logo}
                onChange={(e) => set("logo", e.target.value)}
                className="mt-2 font-mono-num text-xs"
                placeholder="https://yoursite.com/logo.png"
              />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold flex items-center gap-2">
                <Palette size={12} /> Brand color
              </Label>
              <div className="mt-2 flex items-center gap-3">
                <input
                  data-testid="advisor-color"
                  type="color"
                  value={cfg.color}
                  onChange={(e) => set("color", e.target.value)}
                  className="w-12 h-10 rounded-md border border-[#E5E2DC] cursor-pointer"
                />
                <Input
                  value={cfg.color}
                  onChange={(e) => set("color", e.target.value)}
                  className="font-mono-num text-xs flex-1"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-[#1C2B23]">Hide "powered by Centennial"</Label>
              <Switch
                data-testid="advisor-hide-powered"
                checked={cfg.hide_powered}
                onCheckedChange={(v) => set("hide_powered", v)}
              />
            </div>
          </div>

          {/* Live Preview + Snippet */}
          <div className="lg:col-span-7 space-y-5">
            <div className="bg-white border border-[#E5E2DC] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">Live preview</div>
                <a
                  data-testid="advisor-preview-link"
                  href={embedUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#2B5339] hover:underline inline-flex items-center gap-1"
                >
                  Open in new tab <ExternalLink size={11} />
                </a>
              </div>
              <iframe
                data-testid="advisor-preview"
                src={embedUrl}
                title="preview"
                className="w-full h-[460px] rounded-xl border border-[#E5E2DC]"
                loading="lazy"
              />
            </div>

            <div className="rounded-2xl bg-[#1C2B23] text-[#D6DBD7] font-mono text-[12px] leading-relaxed p-5 relative overflow-hidden">
              <div className="absolute top-3 right-3">
                <button
                  data-testid="advisor-copy"
                  onClick={copySnippet}
                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase bg-[#2B5339] hover:bg-[#348C59] text-white rounded-full px-2.5 py-1 transition-colors"
                >
                  {copied ? <Check size={11} /> : <Copy size={11} />}
                  {copied ? "copied" : "copy"}
                </button>
              </div>
              <pre className="whitespace-pre-wrap pr-16">{snippet}</pre>
            </div>
          </div>
        </div>

        {/* Featured listing edit form — appears for $99/yr subscribers */}
        {packageId === "advisor_listing_yearly" && (
          <div id="featured" className="mt-12">
            <ListingForm token={token} packageId={packageId} />
          </div>
        )}
      </section>
    </div>
  );
}
